// combatpro/players/playerEngine.js
//
// Combat PRO - Player Engine (Stage 48C-0: Players Runtime Foundation).
//
// Pure, UI-independent player logic migrated from the legacy viPlayers runtime in
// battletracker.html (8972-9034). No React, no DOM, no context, no state, no side
// effects, no persistence. Receives primitives / plain objects and returns plain
// data. Persistence (the legacy localStorage "vi_players", 8975-8987) is
// intentionally NOT migrated: player state lives in CombatContext only.
//
// LEGACY BASELINE: the original monster cast path did not affect players.
// vi_executeSpellSafe filters out p: targets (battletracker.html 4376) and
// applyDamage / applySave / applyCondition are monster-only (4479 / 4505 / 4563);
// viDamagePlayer / viHealPlayer are driven ONLY by the Players panel's manual
// buttons (9063 / 9065), never by casting. Authority 05 adds player effects through
// the explicit player authority adapter while leaving that protected DM path intact.
//
// Source mapping (battletracker.html):
//   createPlayer     <- viAddPlayer object shape + defaults (8996-8997)
//   findPlayerById   <- viPlayers.find(x => x.id === id) (9006 etc.)
//   getPlayerName    <- player name fallback used by target options (12057)
//   damagePlayerHp   <- viDamagePlayer hp clamp (9007)
//   healPlayerHp     <- viHealPlayer hp clamp (9013)
//   getHpColor       <- viHpColor (9027-9034)
//   abilityModifier  <- inline player-card modifier Math.floor((score-10)/2) (9054)
//
// No optional chaining. No em dash.

// Parse an integer with a fallback (mirrors the parseInt(...) || default pattern
// in viAddPlayer, but treats only non-numeric input as "use the fallback").
function toInt(value, fallback) {
  const n = parseInt(value, 10);
  return isNaN(n) ? fallback : n;
}

// Build a player record. Defaults mirror viAddPlayer (8991-8997): hp / maxHp / ac
// default to 10, initiative to 0, ability scores to 10, notes empty, conditions
// empty. The id is supplied by the caller (the context id counter, mirroring
// viPlayerIdCounter). The empty-name guard lives in the caller (viAddPlayer 8995).
export function createPlayer(data, id) {
  const d = data || {};
  return {
    id: id,
    name: (d.name || '').trim(),
    hp: toInt(d.hp, 10),
    maxHp: toInt(d.maxHp, 10),
    ac: toInt(d.ac, 10),
    initiative: toInt(d.initiative, 0),
    str: 10,
    dex: 10,
    con: 10,
    int: 10,
    wis: 10,
    cha: 10,
    notes: d.notes || '',
    conditions: Array.isArray(d.conditions) ? d.conditions.slice() : [],
    // Authority 05: DM/campaign-authorised character spell capability. These
    // fields travel with the existing player record and encounter persistence;
    // they are not a second character or spell store.
    spells: Array.isArray(d.spells) ? d.spells.slice() : [],
    activeSpells: Array.isArray(d.activeSpells) ? d.activeSpells.slice() : [],
    spellHistory: Array.isArray(d.spellHistory) ? d.spellHistory.slice() : [],
    spellDC: toInt(d.spellDC, 13),
    spellAttackModifier: toInt(d.spellAttackModifier, 0),
    concentrationCastId: d.concentrationCastId || null
  };
}

// Legacy viPlayers.find(x => x.id === id). Returns the player or null.
export function findPlayerById(players, id) {
  const list = players || [];
  for (let i = 0; i < list.length; i++) {
    if (list[i].id === id) { return list[i]; }
  }
  return null;
}

// Legacy player name fallback (battletracker.html 12057): trimmed name, else
// "Player N". Used by target options / pills in the later targeting stage.
export function getPlayerName(player, index) {
  if (player && player.name && player.name.trim()) { return player.name.trim(); }
  return 'Player ' + (Number(index) + 1);
}

// Legacy viDamagePlayer (9007): hp = max(0, hp - amount).
export function damagePlayerHp(player, amount) {
  const hp = (player && typeof player.hp === 'number') ? player.hp : 0;
  const amt = Number(amount) || 0;
  return Math.max(0, hp - amt);
}

// Legacy viHealPlayer (9013): hp = min(maxHp, hp + amount).
export function healPlayerHp(player, amount) {
  const hp = (player && typeof player.hp === 'number') ? player.hp : 0;
  const maxHp = (player && typeof player.maxHp === 'number') ? player.maxHp : hp;
  const amt = Number(amount) || 0;
  return Math.min(maxHp, hp + amt);
}

// Legacy viHpColor (9027-9034): HP bar colour by remaining fraction.
export function getHpColor(hp, maxHp) {
  if (!maxHp) { return '#aaa'; }
  const pct = hp / maxHp;
  if (pct <= 0) { return '#dc2626'; }
  if (pct <= 0.25) { return '#ea580c'; }
  if (pct <= 0.5) { return '#ca8a04'; }
  return '#16a34a';
}

// Legacy player-card ability modifier (battletracker.html 9054). The player card
// computed this inline (Math.floor((score - 10) / 2)) rather than via a shared
// helper, so it is reproduced here to keep the player view self-contained.
export function abilityModifier(score) {
  return Math.floor((Number(score) - 10) / 2);
}
